import { useMemo } from 'react';
import { groupByItem, priceStats, type PriceStats } from '@tahi/core';
import { useData } from '../../data/hooks';
import type { LocalDb } from '../../data/localDb';
import * as repo from '../../data/repos/shopping';
import { useQuery } from '../../data/useQuery';

/** v0.1c's useQuery (re-runs when one of the tables changes), returning just the data. */
function useRead<T>(
  tables: readonly string[],
  read: (db: LocalDb, userId: string) => Promise<T>,
  deps: readonly unknown[],
): T | undefined {
  const { userId } = useData();
  return useQuery(tables, (db) => read(db, userId), [userId, ...deps]).data;
}

const TRIP_TABLES = ['shopping_trips', 'shopping_trip_items'] as const;

export const useOpenItems = () => useRead(['shopping_items'], repo.listOpenItems, []);
export const useItem = (id: string) =>
  useRead(['shopping_items'], (db, u) => repo.getItem(db, u, id), [id]);
export const useTrips = () => useRead(TRIP_TABLES, repo.listTrips, []);
export const useTrip = (id: string) =>
  useRead(TRIP_TABLES, (db, u) => repo.getTrip(db, u, id), [id]);
export const useTripLines = (id: string) =>
  useRead(TRIP_TABLES, (db, u) => repo.listTripLines(db, u, id), [id]);
export const useTripHistory = (id: string) =>
  useRead(['history'], (db, u) => repo.listTripHistory(db, u, id), [id]);
export const usePriceData = () => useRead(TRIP_TABLES, repo.loadPriceData, []);

/** Price stats per normalized item name (last, lowest, average, change, cheapest store). */
export function usePriceStats(): Map<string, PriceStats> | undefined {
  const data = usePriceData();
  return useMemo(() => {
    if (!data) return undefined;
    const out = new Map<string, PriceStats>();
    for (const [key, points] of groupByItem(data.points)) {
      const s = priceStats(points);
      if (s) out.set(key, s);
    }
    return out;
  }, [data]);
}
